import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  UserPlus,
  Trash2,
  Loader,
  AlertCircle,
  Eye,
  EyeOff,
  ShieldMinus,
  ShieldPlus,
  KeyRound,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import config from "./Config/config";
import { useAuth } from "../AuthContext";


const UpdatePasswordPopup = ({ userId, onClose, onUpdate }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  const handleUpdate = () => {
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    onUpdate(userId, newPassword);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <h3 className="text-xl font-semibold mb-4">Update Password</h3>
        <div className="relative mb-4">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
          />
          <button
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <div className="relative mb-4">
          <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
          />
          <button
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {error && <p className="text-red-500 mb-4">{error}</p>}
        <div className="flex justify-end space-x-4">
          <button
            onClick={handleUpdate}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Update
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showUserList, setShowUserList] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const { user, isAuthenticated, getToken, logout } = useAuth();

  const handleApiCall = useCallback(
    async (url, method, body = null) => {
      const token = getToken();
      if (!token) throw new Error("No token available");

      try {
        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: body ? JSON.stringify(body) : null,
        });

        if (response.status === 401) {
          logout();
          throw new Error("Session expired. Please login again.");
        }

        return response;
      } catch (error) {
        console.error("API call error:", error);
        throw error;
      }
    },
    [getToken, logout]
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await handleApiCall(
        `${config.API_BASE_URL}/api/listusers/`,
        "GET"
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      setError(`Failed to fetch users: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [handleApiCall]);

  useEffect(() => {
    if (isAuthenticated() && user?.isAdmin) {
      fetchUsers();
    }
  }, [isAuthenticated, user, fetchUsers]);

  const handleInputChange = (e) => {
    setNewUser({ ...newUser, [e.target.name]: e.target.value });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (newUser.password !== newUser.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await handleApiCall(
        `${config.API_BASE_URL}/api/users/`,
        "POST",
        {
          username: newUser.username,
          email: newUser.email,
          password: newUser.password,
        }
      );
      if (!response.ok) throw new Error("Failed to create user");
      await fetchUsers();
      setNewUser({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
      });
      setSuccessMessage("User created successfully");
    } catch (error) {
      setError(`Failed to create user: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await handleApiCall(
        `${config.API_BASE_URL}/api/users/${userId}/`,
        "DELETE"
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete user");
      }
      await fetchUsers();
      setSuccessMessage("User deleted successfully");
    } catch (error) {
      setError(`Failed to delete user: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMakeAdmin = async (userId) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await handleApiCall(
        `${config.API_BASE_URL}/api/users/${userId}/make-admin/`,
        "PUT"
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to make user admin");
      }
      await fetchUsers();
      setSuccessMessage("User promoted to admin successfully");
    } catch (error) {
      setError(`Failed to make user admin: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAdmin = async (userId) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await handleApiCall(
        `${config.API_BASE_URL}/api/users/${userId}/downgrade-admin/`,
        "PUT"
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to make admin to user");
      }
      await fetchUsers();
      setSuccessMessage("Admin demote to user successfully");
    } catch (error) {
      setError(`Failed to demote admin: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (userId, newPassword) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await handleApiCall(
        `${config.API_BASE_URL}/api/users/${userId}/change-password/${newPassword}/`,
        "PUT"
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to change user password");
      }
      await fetchUsers();
      setSuccessMessage("User Password Updated successfully");
    } catch (error) {
      setError(`Failed to Update User Password: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPopup = (userId) => {
    setSelectedUserId(userId);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedUserId(null);
  };

  const toggleUserList = () => {
    setError(null);
    setSuccessMessage(null);
    setShowUserList(!showUserList);
    if (!showUserList) {
      fetchUsers();
    }
  };

  if (!isAuthenticated()) return <Navigate to="/login" />;
  if (!user?.isAdmin) {
    console.log("user: ", { user });
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
        <p>You do not have admin access to view this panel.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-3xl font-bold mb-6 flex items-center">
        <Users className="mr-2" />
        Admin Panel
      </h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertCircle className="mr-2" />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertCircle className="mr-2" />
          {successMessage}
        </div>
      )}

      <form
        onSubmit={handleCreateUser}
        className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-6"
      >
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <UserPlus className="mr-2" />
          Create New User
        </h3>

        <div className="mb-4">
          <input
            type="text"
            name="username"
            value={newUser.username}
            onChange={handleInputChange}
            placeholder="Username"
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div className="mb-4">
          <input
            type="email"
            name="email"
            value={newUser.email}
            onChange={handleInputChange}
            placeholder="Email"
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div className="mb-4 relative">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={newUser.password}
            onChange={handleInputChange}
            placeholder="Password"
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <div className="mb-6 relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            value={newUser.confirmPassword}
            onChange={handleInputChange}
            placeholder="Confirm Password"
            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center"
        >
          {loading ? (
            <Loader className="animate-spin mr-2" />
          ) : (
            <UserPlus className="mr-2" />
          )}
          Create User
        </button>
      </form>

      <div className="mt-6">
        <button
          onClick={() => setShowUserList(!showUserList)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
        >
          {showUserList ? (
            <EyeOff className="mr-2" />
          ) : (
            <Eye className="mr-2" />
          )}
          {showUserList ? "Hide User List" : "Show User List"}
        </button>

        {showUserList && (
          <div className="mt-4 bg-white shadow-md rounded p-6">
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Users className="mr-2" />
              Users List
            </h3>
            {loading && <Loader className="animate-spin mx-auto" />}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admin
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.userId}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.isAdmin ? "Yes" : "No"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleDeleteUser(user.userId)}
                            className="text-red-600 hover:text-red-900"
                            disabled={loading}
                          >
                            <Trash2 size={18} />
                          </button>
                          {!user.isAdmin && (
                            <button
                              onClick={() => handleMakeAdmin(user.userId)}
                              className="text-blue-600 hover:text-blue-900"
                              disabled={loading}
                            >
                              <ShieldPlus size={18} />
                            </button>
                          )}
                          {user.isAdmin && (
                            <button
                              onClick={() => handleRemoveAdmin(user.userId)}
                              className="text-yellow-600 hover:text-yellow-900"
                              disabled={loading}
                            >
                              <ShieldMinus size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenPopup(user.userId)}
                            className="text-gray-600 hover:text-gray-900"
                            disabled={loading}
                          >
                            <KeyRound size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showPopup && (
        <UpdatePasswordPopup
          userId={selectedUserId}
          onClose={() => setShowPopup(false)}
          onUpdate={handleUpdatePassword}
        />
      )}

     
    </div>
  );
};

export default AdminPanel;
